// Stock Transfer Order Types
// Based on backend API documentation for internal stock transfers between godowns

export type StockTransferStatus = 'draft' | 'in_transit' | 'completed' | 'cancelled';

// Item reference for display
export interface ItemReference {
  id: number;
  name: string;
  sku?: string | null;
  item_type?: string;
  unit?: {
    id: number;
    name: string;
    symbol: string;
  };
}

// Godown/Site reference
export interface GodownReference {
  id: number;
  name: string;
  code?: string;
  site_id?: number;
  site?: {
    id: number;
    name: string;
  };
}

// Stock Transfer Item
export interface StockTransferItem {
  id?: number;
  item_id: number;
  item?: ItemReference;
  quantity: string | number;
  rate: string | number;
  unit_id: number;
  unit?: {
    id: number;
    name: string;
    symbol: string;
  };
  taxable_amount?: string | number;
  tax_amount?: string | number;
  total_amount?: string | number;
  notes?: string;
  current_stock?: number; // For display purposes during creation/edit
}

// Status History Entry
export interface StatusHistoryEntry {
  id: number;
  previous_status: StockTransferStatus | null;
  new_status: StockTransferStatus;
  changed_at: string;
  changed_by: number;
  notes?: string;
  user?: {
    id: number;
    username: string;
    full_name?: string;
  };
}

// Stock Transfer Order - Full details
export interface StockTransferOrder {
  id: number;
  organization_id: number;
  source_godown_id: number;
  destination_godown_id: number;
  source_godown?: GodownReference;
  destination_godown?: GodownReference;
  document_type: 'stock_transfer_order';
  document_number: string;
  reference_number?: string | null;
  transaction_date: string;
  status: StockTransferStatus;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by?: number;
  items: StockTransferItem[];
  status_history?: StatusHistoryEntry[];
}

// Stock Transfer Order - List item (for table display)
export interface StockTransferOrderListItem {
  id: number;
  organization_id: number;
  source_godown_id: number;
  destination_godown_id: number;
  source_godown?: GodownReference;
  destination_godown?: GodownReference;
  document_type: 'stock_transfer_order';
  document_number: string;
  reference_number?: string | null;
  transaction_date: string;
  status: StockTransferStatus;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  items_count?: number; // Number of items in the transfer
  total_value?: string | number; // Total value of transferred items
}

// Create Stock Transfer Order
export interface StockTransferOrderCreate {
  source_godown_id: number;
  destination_godown_id: number;
  document_number?: string; // Optional, auto-generated if not provided
  transaction_date: string;
  status?: StockTransferStatus; // Defaults to 'draft'
  notes?: string;
  reference_number?: string;
  items: {
    item_id: number;
    quantity: string | number;
    rate: string | number;
    unit_id: number;
    notes?: string;
  }[];
}

// Update Stock Transfer Order
export interface StockTransferOrderUpdate {
  source_godown_id?: number;
  destination_godown_id?: number;
  transaction_date?: string;
  notes?: string;
  reference_number?: string;
  items?: {
    id?: number; // Include ID for existing items
    item_id: number;
    quantity: string | number;
    rate: string | number;
    unit_id: number;
    notes?: string;
  }[];
}

// Stock availability check response
export interface StockAvailability {
  item_id: number;
  godown_id: number;
  quantity: number;
  unit_id: number;
  last_updated?: string;
}

// Status update request
export interface StatusUpdateRequest {
  new_status: StockTransferStatus;
  notes?: string;
}

// Helper function to get status color
export const getStatusColor = (status: StockTransferStatus): string => {
  switch (status) {
    case 'draft':
      return 'gray';
    case 'in_transit':
      return 'blue';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'gray';
  }
};

// Helper function to get status label
export const getStatusLabel = (status: StockTransferStatus): string => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'in_transit':
      return 'In Transit';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
};

// Helper to check if status can be edited
export const canEditStatus = (currentStatus: StockTransferStatus): boolean => {
  return currentStatus === 'draft';
};

// Helper to get available next statuses
export const getNextStatuses = (currentStatus: StockTransferStatus): StockTransferStatus[] => {
  switch (currentStatus) {
    case 'draft':
      return ['in_transit', 'cancelled'];
    case 'in_transit':
      return ['completed', 'cancelled'];
    default:
      return [];
  }
};